import React from 'react';
import { AwardIcon, BrainIcon, CheckCircleIcon, DollarSignIcon, GraduationCapIcon, LaptopIcon, LockIcon } from 'lucide-react';
import { ExploreBridge } from '../data/exploreBridges';
import { UniversityData } from '../data/universityData';
const featureIconMap: Record<string, any> = {
  BrainIcon,
  AwardIcon,
  LaptopIcon,
  DollarSignIcon,
  GraduationCapIcon
};
interface BrandedExploreBridgeProps {
  bridge: ExploreBridge;
  university: UniversityData;
  onCta: () => void;
}
// Branded layout for /explore/{brand}-{version} ad copy. Mirrors the
// advertiser page (logo bar, brand-colored hero, stats, features, CTA band)
// but swaps the multi-step form for a single call to action that links out.
export function BrandedExploreBridge({
  bridge,
  university,
  onCta
}: BrandedExploreBridgeProps) {
  const landing = university.landingContent;
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-98" data-mp="2lyetgb6myy7i">
      {/* School logo bar */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-99" data-mp="qiefxw2nqrgs">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-100" data-mp="qkinqp3ogamc">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-101" data-mp="10gvdjbw3q4bt" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-102" data-mp="7sgc5s5w7kiy">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-103" data-mp="gsuymuulf5o1">
            <LockIcon className="w-3.5 h-3.5" data-id="element-104" data-mp="ljl3m6z3x6i7" />
            Secure
          </div>
        </div>
      </header>

      <main className="flex-grow bg-white" data-id="element-105" data-mp="3l5rttb8026xg">
        {/* Hero — brand color band with the CTA card in place of the form */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-106" data-mp="3p6v47i7a3ara">
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-107" data-mp="165kamffwdu5g">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-108" data-mp="116r5baarl9gd">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-109" data-mp="dgwqin6vxath">
              {landing?.headline || university.tagline}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-110" data-mp="mdnmz0pcmrol">
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-111" data-mp="1xux3bed6l3lo">
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-112" data-mp="1xt3vhvqu0dc3">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-113" data-mp="21d4cang1ak76">
                {landing?.headline || university.tagline}
              </h1>
              {(landing?.subheadline || university.mission) && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-114" data-mp="1rgrgnk0xhw9s">
                  {landing?.subheadline || university.mission}
                </p>}
              {landing?.heroHighlights && landing.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-115" data-mp="2p1vymy6y08c1">
                  {landing.heroHighlights.map(highlight => <li key={highlight} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-116" data-mp="2abg8d4zo6a70">
                      <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-117" data-mp="330n5q0ne0tqo" />
                      <span data-id="element-118" data-mp="37uhngyjfmyn4">{highlight}</span>
                    </li>)}
                </ul>}
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2 text-center" data-id="element-119" data-mp="1f5iglwuyhx75">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight" data-id="element-120" data-mp="1a7gsuyml59pc">
                {bridge.cardHeadline}
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed" data-id="element-121" data-mp="159p2wa5i3xns">
                {bridge.cardSubhead}
              </p>
              <button onClick={onCta} className="mt-8 w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2" style={{
              backgroundColor: university.ctaColor,
              color: university.ctaTextColor || '#ffffff'
            }} data-id="element-122" data-mp="1xw9w7g5jejtz">
                {bridge.ctaLabel}
              </button>
              <p className="mt-4 text-xs text-gray-400" data-id="element-123" data-mp="2gtjegtz4lr9n">
                {university.accreditation}
              </p>
            </div>
          </div>
        </section>

        {/* Stats strip */}
        {landing?.stats && landing.stats.length > 0 && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-124" data-mp="2y3n8xo956jik">
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-125" data-mp="333002l71qcqy">
              {landing.stats.map(stat => <div key={stat.label} className="text-center" data-id="element-126" data-mp="381uqo9b995lm">
                  <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
              color: university.primaryColor
            }} data-id="element-127" data-mp="3c3fwxbtaalmu">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-128" data-mp="3lt9aujgwfcu9">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </section>}

        {/* Short paragraph section */}
        <section className="bg-gray-50 py-16 md:py-20 px-4" data-id="element-129" data-mp="364kz1xugc09t">
          <div className="max-w-3xl mx-auto text-center" data-id="element-130" data-mp="314ymzrqd0q2l">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
            color: university.ctaColor
          }} data-id="element-131" data-mp="316jet9lahuwr">
              Why {university.shortName}
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-8" data-id="element-132" data-mp="3b0tbv4l6fhdt">
              {bridge.sectionHeading}
            </h2>
            <div className="space-y-6 text-left md:text-center" data-id="element-133" data-mp="3ju6amn5x9d7t">
              {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-134" data-mp="2mkoqc1fbit1">
                  {paragraph}
                </p>)}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        {landing?.features && landing.features.length > 0 && <section className="bg-white py-16 md:py-20 px-4" data-id="element-135" data-mp="eyd8ktdblwio">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-136" data-mp="jwf2688dhe8e">
              {landing.features.map(feature => {
            const Icon = featureIconMap[feature.icon] || GraduationCapIcon;
            return <div key={feature.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-137" data-mp="a50ydo9e1s9g">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                backgroundColor: `${university.primaryColor}14`
              }} data-id="element-138" data-mp="9qwr1mwjmw7">
                      <Icon className="w-7 h-7" style={{
                  color: university.primaryColor
                }} data-id="element-139" data-mp="1gmxhqocmxgq8" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-140" data-mp="8ewd8ikt2bzx">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-141" data-mp="4d2i0ptdq075">
                      {feature.description}
                    </p>
                  </div>;
          })}
            </div>
          </section>}

        {/* Closing CTA band */}
        <section className="py-16 md:py-20 px-4" style={{
        backgroundColor: university.primaryColor
      }} data-id="element-142" data-mp="3ltkcjzd3tc13">
          <div className="max-w-4xl mx-auto text-center text-white" data-id="element-143" data-mp="3gto9c2c8doml">
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-144" data-mp="3csm494vmuicp">
              {landing?.finalCta?.headline || bridge.cardHeadline}
            </h2>
            {landing?.finalCta?.body && <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-145" data-mp="1b20x9xaqika3">
                {landing.finalCta.body}
              </p>}
            <button onClick={onCta} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white" style={{
            backgroundColor: university.ctaColor,
            color: university.ctaTextColor || '#ffffff'
          }} data-id="element-146" data-mp="1dm74cdunaquv">
              {landing?.finalCta?.buttonLabel || bridge.ctaLabel}
            </button>
          </div>
        </section>

        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-147" data-mp="zlmjzd930mz9">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-148" data-mp="14kzduhydeubc">
            <p data-id="element-149" data-mp="14nllehl9h9tk">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-150" data-mp="2g0xtj4o20elj">
              {university.accreditation} • Founded {university.founded}
            </p>
            {bridge.disclosure && <p className="text-gray-500 max-w-2xl mx-auto pt-2" data-id="element-151" data-mp="2k4capynqb10k">
                {bridge.disclosure}
              </p>}
          </div>
        </footer>
      </main>
    </div>;
}